import { createEntityAdapter as reduxCreateEntityAdapter, createDraftSafeSelector } from '@reduxjs/toolkit';
import type { EntityStateAdapter } from '@reduxjs/toolkit';

type EntityId = string;

type Comparer<T> = (a: T, b: T) => number;

type IdSelector<T> = (model: T) => EntityId;

interface EntityDefinition<T> {
  selectId?: IdSelector<T>;
  sortComparer?: Comparer<T>;
}

interface DictionaryNum<T> {
  [id: number]: T | undefined;
}

interface Dictionary<T> extends DictionaryNum<T> {
  [id: string]: T | undefined;
}

interface EntityState<T> {
  ids: EntityId[];
  entities: Dictionary<T>;
  status: boolean;
}

interface LoadingInterface {
  status: boolean;
}

interface EntitySelectors<T, V> {
  selectIds: (state: V) => EntityId[];
  selectEntities: (state: V) => Dictionary<T>;
  selectAll: (state: V) => T[];
  selectTotal: (state: V) => number;
  selectById: (state: V, id: EntityId) => T | undefined;
  selectStatus: (state: V) => boolean;
}

export interface EntityAdapter<T> extends EntityStateAdapter<T, string> {
  selectId: IdSelector<T>;
  sortComparer: false | Comparer<T>;
  getInitialState(): EntityState<T>;
  getInitialState<S extends object>(state: S): EntityState<T> & S;
  getSelectors(): EntitySelectors<T, EntityState<T>>;
  getSelectors<V>(
    selectState: (state: V) => EntityState<T>
  ): EntitySelectors<T, V>;
}

export const createEntityAdapter = <T>(options?: EntityDefinition<T>) => {
  const adapterOptions = options || {} as EntityDefinition<T>;

  const adapter = reduxCreateEntityAdapter<T, string>(adapterOptions as any);
  const { getInitialState, getSelectors } = adapter;

  const newGetInitialState = <S extends object>(state?: S): EntityState<T> & S & LoadingInterface => {
    const newState = state || {} as S;

    return getInitialState<S & LoadingInterface>({
      ...newState,
      status: false,
    });
  };

  const newGetSelectors = <V>(selectState: (state: V) => EntityState<T>): EntitySelectors<T, any> => {
    const selectors = getSelectors<V>(selectState);

    const selectStatus = createDraftSafeSelector(selectState, (state: EntityState<T>) => state.status);

    return {
      ...selectors,
      selectStatus,
    };
  };

  const newAdapter = {
    ...adapter,
    getInitialState: newGetInitialState,
    getSelectors: newGetSelectors,
  } as EntityAdapter<T>;

  return newAdapter;
};
